import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  DeployMetricsApi,
  MetricsQuery,
  AggregatedMetrics,
  ProjectInfo,
  SyncOptions,
  SyncStatus,
} from './DeployMetricsApi';

export class DeployMetricsClient implements DeployMetricsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('deploy-metrics');
  }

  private async request<T>(path: string): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const res = await this.fetchApi.fetch(`${baseUrl}${path}`);
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as any;
      throw new Error(
        body?.error?.message ?? `Request failed with status ${res.status}`,
      );
    }
    return res.json() as Promise<T>;
  }

  async getMetrics(query?: MetricsQuery): Promise<AggregatedMetrics> {
    const qs = new URLSearchParams();
    if (query?.projectId !== undefined)
      qs.set('projectId', String(query.projectId));
    if (query?.startDate) qs.set('startDate', query.startDate);
    if (query?.endDate) qs.set('endDate', query.endDate);
    if (query?.ref) qs.set('ref', query.ref);
    if (query?.environments && query.environments.length > 0)
      qs.set('environments', query.environments.join(','));
    if (query?.excludeOwner) qs.set('excludeOwner', query.excludeOwner);
    if (query?.excludeOwners && query.excludeOwners.length > 0)
      qs.set('excludeOwners', query.excludeOwners.join(','));
    if (query?.includeOwner) qs.set('includeOwner', query.includeOwner);
    const queryString = qs.toString();
    return this.request(`/metrics${queryString ? `?${queryString}` : ''}`);
  }

  async getProjects(owner?: string): Promise<ProjectInfo[]> {
    const path = owner ? `/projects?owner=${encodeURIComponent(owner)}` : '/projects';
    return this.request(path);
  }

  async triggerSync(options?: SyncOptions): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const res = await this.fetchApi.fetch(`${baseUrl}/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reset: options?.reset ?? false }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as any;
      throw new Error(
        body?.error?.message ?? `Sync failed with status ${res.status}`,
      );
    }
  }

  async getSyncStatus(): Promise<SyncStatus> {
    return this.request('/sync/status');
  }
}
